/**
 * Advisory Team Card
 * Displays all advisory team members from the businesses table
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Users, Edit, ChevronDown, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdvisoryTeamCard = ({ businessProfile, isExpanded = true, onToggle }) => {
  const navigate = useNavigate();

  // Define advisor types and their labels
  const advisorTypes = [
    { key: 'attorney', label: 'Business Attorney', hasKey: 'has_attorney' },
    { key: 'accountant', label: 'CPA / Accountant', hasKey: 'has_accountant' },
    { key: 'financial_advisor', label: 'Financial Advisor', hasKey: 'has_financial_advisor' },
    { key: 'exit_advisor', label: 'Exit / M&A Advisor', hasKey: 'has_exit_advisor' },
    { key: 'insurance_agent', label: 'Insurance Agent', hasKey: 'has_insurance_agent' },
    { key: 'banker', label: 'Banker / Lender', hasKey: 'has_banker' },
    { key: 'estate_planner', label: 'Estate Planner', hasKey: 'has_estate_planner' },
    { key: 'business_coach', label: 'Business Coach', hasKey: 'has_business_coach' }
  ];

  // Get all advisors (both standard and custom)
  const getAllAdvisors = () => {
    const advisors = [];

    // Add standard advisors
    advisorTypes.forEach(({ key, label, hasKey }) => {
      if (businessProfile?.[hasKey] && businessProfile?.[`${key}_name`]) {
        advisors.push({
          type: 'standard',
          role: label,
          name: businessProfile[`${key}_name`],
          email: businessProfile[`${key}_email`],
          phone: businessProfile[`${key}_phone`]
        });
      }
    });

    // Add custom advisors
    if (businessProfile?.custom_advisors && Array.isArray(businessProfile.custom_advisors)) {
      businessProfile.custom_advisors.forEach(advisor => {
        if (advisor && (advisor.name || advisor.title || advisor.email || advisor.phone)) {
          advisors.push({
            type: 'custom',
            role: advisor.title || 'Custom Advisor',
            name: advisor.name,
            email: advisor.email,
            phone: advisor.phone
          });
        }
      });
    }

    return advisors;
  };

  const advisors = getAllAdvisors();
  const hasAdvisors = advisors.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onToggle}
            className="flex items-center space-x-2 flex-1 text-left hover:opacity-80 transition"
          >
            <ChevronDown
              className={`text-teal-600 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
              size={18}
            />
            <Users className="text-teal-600" size={20} />
            <h3 className="text-base font-bold text-gray-900">Advisory Team</h3>
            {advisors.length > 0 && (
              <span className="ml-2 bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-xs font-bold">
                {advisors.length}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/business-profile?section=advisory')}
            className="p-1.5 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
            title="Edit Advisory Team"
          >
            <Edit size={16} />
          </button>
        </div>

        {isExpanded && (
          <div>
            {hasAdvisors ? (
              <div className="space-y-1 text-xs">
                {advisors.map((advisor, index) => (
                  <div key={index} className="border-b border-gray-100 last:border-0 py-2">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <span className="text-gray-600 text-xs font-medium flex-shrink-0">{advisor.role}:</span>
                      <span className="font-semibold text-gray-900 text-right">{advisor.name || 'N/A'}</span>
                    </div>
                    {advisor.email && (
                      <div className="flex items-center gap-1 text-xs text-gray-600 ml-0 mt-0.5">
                        <Mail size={10} className="text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${advisor.email}`} className="text-blue-600 hover:underline truncate">
                          {advisor.email}
                        </a>
                      </div>
                    )}
                    {advisor.phone && (
                      <div className="flex items-center gap-1 text-xs text-gray-600 ml-0 mt-0.5">
                        <Phone size={10} className="text-gray-400 flex-shrink-0" />
                        <a href={`tel:${advisor.phone}`} className="text-blue-600 hover:underline">
                          {advisor.phone}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Users size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-xs text-gray-500 mb-2">No advisory team members added yet</p>
                <button
                  onClick={() => navigate('/business-profile?section=advisory')}
                  className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition text-xs font-semibold"
                >
                  Add Advisors
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

AdvisoryTeamCard.propTypes = {
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  businessProfile: PropTypes.shape({
    has_attorney: PropTypes.bool,
    attorney_name: PropTypes.string,
    attorney_email: PropTypes.string,
    attorney_phone: PropTypes.string,
    has_accountant: PropTypes.bool,
    accountant_name: PropTypes.string,
    accountant_email: PropTypes.string,
    accountant_phone: PropTypes.string,
    has_financial_advisor: PropTypes.bool,
    financial_advisor_name: PropTypes.string,
    financial_advisor_email: PropTypes.string,
    financial_advisor_phone: PropTypes.string,
    has_exit_advisor: PropTypes.bool,
    exit_advisor_name: PropTypes.string,
    exit_advisor_email: PropTypes.string,
    exit_advisor_phone: PropTypes.string,
    has_insurance_agent: PropTypes.bool,
    insurance_agent_name: PropTypes.string,
    insurance_agent_email: PropTypes.string,
    insurance_agent_phone: PropTypes.string,
    has_banker: PropTypes.bool,
    banker_name: PropTypes.string,
    banker_email: PropTypes.string,
    banker_phone: PropTypes.string,
    has_estate_planner: PropTypes.bool,
    estate_planner_name: PropTypes.string,
    estate_planner_email: PropTypes.string,
    estate_planner_phone: PropTypes.string,
    has_business_coach: PropTypes.bool,
    business_coach_name: PropTypes.string,
    business_coach_email: PropTypes.string,
    business_coach_phone: PropTypes.string,
    custom_advisors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        title: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string
      })
    )
  })
};

export default AdvisoryTeamCard;
